// RestoreDialog — admin-only. Upload a .sql/.sql.gz dump and execute it against a
// target database. Destructive (runs DROP/CREATE/INSERT), so the Restore button
// is armed only once the user types the exact target DB name. Four phases:
// config → uploading → preflight (review measured stats + opt into auto-tune) →
// ProgressModal (background execution, with the pause-on-error skip/skip-all/abort prompt).
//
// Props:
//   connectionId  number
//   database      string   — pre-selected target (the DB the user right-clicked); may be ''
//   onClose       fn()
import { createSignal, createResource, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import ProgressModal from './ProgressModal.jsx';
import CodeMirrorSQL from './CodeMirrorSQL.jsx';
import { api } from '../lib/api.js';
import {
  prepareRestoreUpload, startRestore, cancelRestorePrepare,
  resolveRestore, prepareRestorePostSQL, RESTORE_STAMP_SQL,
} from '../lib/restore.js';
import { toast } from '../lib/toast.js';
import { sqlSchemaFor } from '../lib/schema.js';

// formatBytes — short human form for the preflight numbers.
const formatBytes = (n) => {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MiB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
};

// RESTORE_STAMP_SQL (the "Append restore timestamp" block) is shared from
// lib/restore.js so this dialog and ServerRestoreDialog never drift.

export default function RestoreDialog(props) {
  const [databases] = createResource(
    () => props.connectionId,
    async (id) => {
      // Endpoint returns { databases: [...] }; unwrap to the array for the <For>.
      try { const r = await api.get(`/api/connections/${id}/databases`); return r?.databases || []; }
      catch { return []; }
    },
  );

  // Easy mode (per-user flag or Shift) — a minimal, footgun-reduced flow:
  // existing-DB only, locked to the clicked database, no type-to-confirm, no
  // create-new / retarget / post-SQL editor / warning, with the restore-stamp
  // forced on. props.database is always set when easy (the menu only offers
  // "restore into THIS database").
  const easy = !!props.easy;

  const [file, setFile] = createSignal(null);
  // Easy mode is always existing-DB; otherwise default by whether a target was given.
  const [mode, setMode] = createSignal(easy || props.database ? 'existing' : 'new'); // 'existing' | 'new'
  const [existingDb, setExistingDb] = createSignal(props.database || '');
  const [newDb, setNewDb] = createSignal('');
  const [confirmText, setConfirmText] = createSignal('');
  // Easy mode forces the ninocat_restore marker on (editor hidden), so seed the
  // post-SQL with the block up front.
  const [postSql, setPostSql] = createSignal(easy ? RESTORE_STAMP_SQL : '');
  const [retarget, setRetarget] = createSignal(false); // strip src-db qualifiers (foreign dumps)
  const [strict, setStrict]     = createSignal(false); // verify after restore (manifest-backed only)
  const canStrict = () => !!prep()?.hasCorrectness;
  const [stampRestore, setStampRestore] = createSignal(easy); // append the ninocat_restore marker block

  // Toggle the restore-timestamp block in/out of the post-SQL editor without
  // clobbering any custom SQL the user typed. Append (newline-separated) when
  // turning on; strip the exact block + tidy blank lines when turning off.
  const toggleStampRestore = (on) => {
    setStampRestore(on);
    if (on) {
      setPostSql((prev) => {
        if (prev.includes(RESTORE_STAMP_SQL)) return prev;        // already there
        return prev.trim() ? prev.trimEnd() + '\n\n' + RESTORE_STAMP_SQL : RESTORE_STAMP_SQL;
      });
    } else {
      setPostSql((prev) =>
        prev.split(RESTORE_STAMP_SQL).join('').replace(/\n{3,}/g, '\n\n').trim());
    }
  };

  const [uploadPct, setUploadPct] = createSignal(null); // non-null while uploading
  // After upload completes, prep holds the server's measurement + probe result.
  // The user can then choose autoTune (if the server probe allows it) and click
  // Start — only at that point does the destructive restore actually begin.
  const [prep, setPrep] = createSignal(null);
  // autoTune is initialised based on the prep result: ON by default when the
  // dump's biggest statement would exceed the server cap AND we have SUPER.
  // The user can override either way.
  const [autoTune, setAutoTune] = createSignal(false);
  const [starting, setStarting] = createSignal(false);
  // Required before Start is enabled when creating the database AND the target
  // server's version can't support the dump's charset (dbcaps.Check) — the
  // "block, with override" policy. Only matters in 'new' mode: CREATE DATABASE
  // (and its charset clause) never runs when restoring into an existing db.
  const [overrideAck, setOverrideAck] = createSignal(false);
  const dbViolations = () => (mode() === 'new' ? (prep()?.violations || []) : []);
  const startBlocked = () => dbViolations().length > 0 && !overrideAck();
  const [token, setToken] = createSignal(null);         // set once execution begins
  // Captured at start time so the ProgressModal can show filename / from / into
  // (the prep signal is cleared once the job is running). Survives until the
  // dialog closes.
  const [runMeta, setRunMeta] = createSignal(null);

  const targetDb = () => (mode() === 'new' ? newDb().trim() : existingDb());
  const sqlSchema = sqlSchemaFor(() => props.connectionId, targetDb); // post-SQL autocomplete
  // Type-to-confirm is the advanced-mode guard; easy mode skips it (the restore
  // is locked to the database the user explicitly right-clicked).
  const confirmed = () => easy || confirmText() === targetDb();
  const canUpload = () =>
    !!file() && !!targetDb() && confirmed() && uploadPct() === null && !prep();

  // The headline: will this restore likely fail at packet time?
  const willOverflow = () => {
    const p = prep();
    if (!p) return false;
    if (!p.serverMaxAllowedPacket) return false; // probe failed → we can't tell
    return p.maxStmtBytes > p.serverMaxAllowedPacket;
  };

  // Begin upload + prepare. Doesn't start the destructive restore yet.
  const upload = async () => {
    setUploadPct(0);
    try {
      const r = await prepareRestoreUpload(
        props.connectionId, targetDb(), file(),
        (loaded, total) => setUploadPct(total ? Math.round((loaded / total) * 100) : 0),
      );
      setPrep(r);
      // Default the checkbox ON when the dump would overflow AND we have SUPER.
      // The user can untick if they've already widened it server-side.
      if (r?.hasSuperPrivilege && r?.maxStmtBytes > (r?.serverMaxAllowedPacket || 0)) {
        setAutoTune(true);
      }
    } catch (e) {
      toast.error('Upload failed: ' + (e?.message || e));
    } finally {
      setUploadPct(null);
    }
  };

  // Commit step — fire the actual restore using the held prepareToken.
  const start = async () => {
    setStarting(true);
    try {
      let postSqlToken = '';
      if (postSql().trim()) postSqlToken = await prepareRestorePostSQL(postSql());
      const r = await startRestore(props.connectionId, targetDb(), {
        prepareToken: prep().prepareToken,
        createDb: mode() === 'new',
        retarget: retarget(),
        autoTune: autoTune(),
        strict: canStrict() && strict(),
        postSqlToken,
        override: overrideAck(),
      });
      // Snapshot the meta info before clearing prep — these stay on the
      // ProgressModal for the run's duration. originDatabase is "" if no
      // known marker matched in the dump's head.
      setRunMeta({
        filename: file()?.name || '',
        originDatabase: prep()?.originDatabase || '',
        originConnection: prep()?.originConnection || '',
        originUsername: prep()?.originUsername || '',
        originGeneratedAt: prep()?.originGeneratedAt || '',
        targetDatabase: targetDb(),
        targetConnection: props.connectionName || '',
      });
      setToken(r.token); // execution started → switch to ProgressModal
      setPrep(null);
    } catch (e) {
      toast.error('Restore failed to start: ' + (e?.message || e));
    } finally {
      setStarting(false);
    }
  };

  // Cancel from the preflight step → release the server-side prep entry.
  const cancelPrep = async () => {
    const tok = prep()?.prepareToken;
    setPrep(null);
    if (tok) await cancelRestorePrepare(tok);
  };

  // Cancel from the dialog as a whole → also release prep if one is held.
  const closeAll = async () => {
    const tok = prep()?.prepareToken;
    if (tok) cancelRestorePrepare(tok); // fire-and-forget
    props.onClose?.();
  };

  // Execution phase — the shared progress modal, with the pause decision wired.
  return (
    <Show when={!token()} fallback={
      <ProgressModal
        token={token()}
        title={`Restoring into — ${targetDb()}`}
        kind="restore"
        meta={[
          // Empty fields (a foreign dump carries no origin conn / user) are
          // filtered out by ProgressModal, so a non-Ninocat dump just shows
          // File / Into conn / Into DB.
          { label: 'File',          value: runMeta()?.filename,          mono: true },
          { label: 'Origin conn',   value: runMeta()?.originConnection,  mono: true },
          { label: 'Backed up by',  value: runMeta()?.originUsername,    mono: true },
          { label: 'Backed up at',  value: runMeta()?.originGeneratedAt, mono: true },
          { label: 'From DB',       value: runMeta()?.originDatabase,    mono: true },
          { label: 'Into conn',     value: runMeta()?.targetConnection,  mono: true },
          { label: 'Into DB',       value: runMeta()?.targetDatabase,    mono: true },
        ]}
        onResolve={(action) => resolveRestore(token(), action)}
        onClose={props.onClose}
      />
    }>
      <Modal open={true} title={easy ? `Restore into — ${props.database}` : 'Restore database from a dump'} onClose={closeAll}>
        <div class="restore-dialog">
          {/* The destructive-warning banner is advanced-mode only — easy mode
              keeps the surface calm; the restore is still locked to the clicked DB. */}
          <Show when={!easy}>
            <div class="restore-warning">
              ⚠ This runs the uploaded SQL (including <span class="mono">DROP TABLE</span> /
              <span class="mono"> CREATE</span>) against the target database. Existing objects
              with the same names will be replaced. This cannot be undone.
            </div>
          </Show>

          {/* File */}
          <label class="restore-field">
            <span class="restore-field-label">Dump file (.sql or .sql.gz)</span>
            <input
              type="file"
              accept=".sql,.gz,application/sql,application/gzip"
              disabled={uploadPct() !== null || !!prep()}
              onChange={(e) => setFile(e.currentTarget.files?.[0] || null)}
            />
          </label>

          {/* Easy mode: a single read-only line showing where it's going — no
              picker, no create-new, no type-to-confirm. */}
          <Show when={easy}>
            <div class="restore-field">
              <span class="restore-field-label">Restoring into</span>
              <div class="mono">{props.connectionName ? `${props.connectionName} · ` : ''}{props.database}</div>
            </div>
          </Show>

          {/* Target database (advanced) */}
          <Show when={!easy}>
          <div class="restore-field">
            <span class="restore-field-label">Restore into</span>
            <div class="restore-target">
              <label class="restore-radio">
                <input type="radio" name="rstmode" checked={mode() === 'existing'}
                       disabled={uploadPct() !== null || !!prep()}
                       onChange={() => setMode('existing')} />
                <span>Existing database</span>
              </label>
              <select
                class="input"
                disabled={mode() !== 'existing' || uploadPct() !== null || !!prep()}
                value={existingDb()}
                onChange={(e) => setExistingDb(e.currentTarget.value)}
              >
                <option value="" disabled>Select…</option>
                <For each={databases() || []}>
                  {(d) => <option value={d.name} selected={d.name === existingDb()}>{d.name}</option>}
                </For>
              </select>
            </div>
            <div class="restore-target">
              <label class="restore-radio">
                <input type="radio" name="rstmode" checked={mode() === 'new'}
                       disabled={uploadPct() !== null || !!prep()}
                       onChange={() => setMode('new')} />
                <span>Create new database</span>
              </label>
              <input
                class="input"
                placeholder="new_database_name"
                disabled={mode() !== 'new' || uploadPct() !== null || !!prep()}
                value={newDb()}
                onInput={(e) => setNewDb(e.currentTarget.value)}
              />
            </div>
          </div>
          </Show>

          {/* Type-to-confirm (advanced only — easy mode is locked to the
              clicked DB, so there's nothing to mistype). */}
          <Show when={!easy}>
          <label class="restore-field">
            <span class="restore-field-label">
              Type <span class="mono restore-confirm-name">{targetDb() || '…'}</span> to confirm
            </span>
            <input
              class="input"
              placeholder="target database name"
              disabled={!targetDb() || uploadPct() !== null || !!prep()}
              value={confirmText()}
              onInput={(e) => setConfirmText(e.currentTarget.value)}
            />
          </label>
          </Show>

          {/* Retarget object names — for foreign dumps (e.g. Navicat) that qualify
              CREATE TRIGGER/PROCEDURE/FUNCTION/VIEW/EVENT names with the SOURCE db.
              Advanced only. */}
          <Show when={!easy}>
          <label class="restore-field restore-opt">
            <input
              type="checkbox"
              checked={retarget()}
              disabled={uploadPct() !== null || !!prep()}
              onChange={(e) => setRetarget(e.currentTarget.checked)}
            />
            <span>
              <span class="restore-field-label" style="display:inline;">Retarget object names to this database</span>
              <span class="muted" style="display:block;font-size:11px;">
                Strips a source-database prefix from CREATE/DROP trigger · procedure · function · view · event
                names (e.g. Navicat's <span class="mono">`olddb`.`tr_x`</span>) so they restore into this
                database instead of colliding in the original. Ninocat's own backups don't need this.
              </span>
            </span>
          </label>
          </Show>

          {/* Append restore timestamp — drops the ninocat_restore marker block
              into the post-SQL editor below. Advanced only; easy mode forces it
              on (the block is seeded into postSql up front, editor hidden). */}
          <Show when={!easy}>
          <label class="restore-field restore-opt">
            <input
              type="checkbox"
              checked={stampRestore()}
              disabled={uploadPct() !== null || !!prep()}
              onChange={(e) => toggleStampRestore(e.currentTarget.checked)}
            />
            <span>
              <span class="restore-field-label" style="display:inline;">Append restore timestamp to database</span>
              <span class="muted" style="display:block;font-size:11px;">
                Adds SQL below that creates <span class="mono">ninocat_restore</span> (if absent) and
                inserts a <span class="mono">NOW()</span> row — a durable, in-database record of this
                restore, written only on success. Edit the SQL freely after ticking.
              </span>
            </span>
          </label>
          </Show>

          {/* Optional post-restore SQL — advanced only. */}
          <Show when={!easy}>
          <div class="restore-field">
            <span class="restore-field-label">Run SQL after a successful restore (optional)</span>
            <div class="restore-postsql">
              <CodeMirrorSQL value={postSql()}
                             schema={sqlSchema}
                             onInput={(v) => { setPostSql(v); setStampRestore(v.includes(RESTORE_STAMP_SQL)); }}
                             height="120px"
                             placeholder="e.g. CALL rebuild_caches();  — runs once the restore finishes (multiple statements OK)" />
            </div>
            <span class="muted" style="font-size:11px;">
              Runs on the server right after the restore finishes — including when you Skip past errors, but not if you Abort.
            </span>
          </div>
          </Show>

          {/* Upload progress */}
          <Show when={uploadPct() !== null}>
            <div class="progress-track">
              <div class="progress-fill" style={`width:${uploadPct()}%`} />
            </div>
            <div class="muted" style="font-size:12px;">Uploading & measuring… {uploadPct()}%</div>
          </Show>

          {/* Preflight — shown after a successful upload + measurement. The user
              reviews stats + the auto-tune choice before the destructive run. */}
          <Show when={prep()}>
            <div class="pk-confirm-diff" style="margin-top:4px;">
              <Show when={prep().originConnection}>
                <div class="pk-confirm-diff-row">
                  <span class="pk-confirm-col">Origin connection</span>
                  <span class="mono">{prep().originConnection}</span>
                </div>
              </Show>
              <Show when={prep().originUsername}>
                <div class="pk-confirm-diff-row">
                  <span class="pk-confirm-col">Backed up by</span>
                  <span class="mono">{prep().originUsername}</span>
                </div>
              </Show>
              <Show when={prep().originGeneratedAt}>
                <div class="pk-confirm-diff-row">
                  <span class="pk-confirm-col">Backed up at</span>
                  <span class="mono">{prep().originGeneratedAt}</span>
                </div>
              </Show>
              <Show when={prep().originDatabase}>
                <div class="pk-confirm-diff-row">
                  <span class="pk-confirm-col">Origin database</span>
                  <span class="mono">{prep().originDatabase}</span>
                </div>
              </Show>
              <div class="pk-confirm-diff-row">
                <span class="pk-confirm-col">Into</span>
                <span class="mono">{props.connectionName ? `${props.connectionName} · ` : ''}{targetDb()}</span>
              </div>
              {/* Size/packet stats are advanced-mode detail; easy mode just needs
                  the provenance + "into" line. */}
              <Show when={!easy}>
              <div class="pk-confirm-diff-row">
                <span class="pk-confirm-col">Statements</span>
                <span class="mono">{prep().total.toLocaleString()}</span>
              </div>
              <div class="pk-confirm-diff-row">
                <span class="pk-confirm-col">Biggest single statement</span>
                <span class="mono">{formatBytes(prep().maxStmtBytes)}</span>
              </div>
              <div class="pk-confirm-diff-row">
                <span class="pk-confirm-col">Server <span class="mono">max_allowed_packet</span></span>
                <span class="mono">
                  {prep().serverMaxAllowedPacket
                    ? formatBytes(prep().serverMaxAllowedPacket)
                    : <span class="muted">unknown</span>}
                </span>
              </div>
              </Show>
            </div>

            {/* Version-compatibility violations — the target's server version can't
                support the dump's database charset (only matters when we're about
                to CREATE the database). Must be acknowledged before Start. */}
            <Show when={dbViolations().length > 0}>
              <div class="pk-confirm-warn" role="alert" style="margin-top:6px;">
                ⛔ The target server's version can't support {dbViolations().length === 1 ? 'this' : 'these'}:
                <ul style="margin:4px 0 6px;padding-left:20px;">
                  <For each={dbViolations()}>{(v) => <li>{v.reason}</li>}</For>
                </ul>
                <label class="restore-field" style="font-weight:600;">
                  <input type="checkbox" checked={overrideAck()} onChange={(e) => setOverrideAck(e.currentTarget.checked)} />
                  I understand this may be rejected by the server — restore anyway
                </label>
              </div>
            </Show>

            {/* Overflow warning — shown only when it would ACTUALLY fail, i.e.
                the dump overflows AND auto-tune isn't going to fix it (no SUPER,
                or the user unticked it). In easy mode autoTune is auto-enabled
                when possible, so this only surfaces the genuinely-stuck case. */}
            <Show when={willOverflow() && !autoTune()}>
              <div class="pk-confirm-warn" role="alert" style="margin-top:6px;">
                ⚠ The biggest statement in this dump
                ({formatBytes(prep().maxStmtBytes)}) exceeds the server's
                <span class="mono"> max_allowed_packet </span>
                ({formatBytes(prep().serverMaxAllowedPacket)}). This restore will
                likely fail mid-way with “MySQL server has gone away”.
                <Show when={!prep().hasSuperPrivilege}> Ask a DBA to raise
                  <span class="mono"> max_allowed_packet</span>, or re-export via Ninocat.</Show>
              </div>
            </Show>

            {/* Auto-tune checkbox — advanced only. In easy mode it's decided
                silently (enabled on upload when the dump overflows + SUPER). */}
            <Show when={!easy}>
            <label class="restore-field restore-opt" style="margin-top:6px;">
              <input
                type="checkbox"
                checked={autoTune()}
                disabled={!prep().hasSuperPrivilege || starting()}
                onChange={(e) => setAutoTune(e.currentTarget.checked)}
              />
              <span>
                <span class="restore-field-label" style="display:inline;">
                  Auto-tune <span class="mono">max_allowed_packet</span> for this restore
                </span>
                <span class="muted" style="display:block;font-size:11px;">
                  {prep().hasSuperPrivilege
                    ? <>Bumps the server's global to <span class="mono">{formatBytes(prep().targetMaxAllowedPacket || (64 << 20))}</span> before the restore, reverts it afterwards. <b>Affects other connections to this MySQL while the restore runs.</b></>
                    : <>Disabled — this MySQL account lacks the SUPER (or SYSTEM_VARIABLES_ADMIN) privilege required to set the global. Ask a DBA to bump <span class="mono">max_allowed_packet</span> server-side, or re-export the dump via Ninocat (its statements stay under 1 MiB).</>}
                </span>
              </span>
            </label>
            <label class="restore-field restore-opt" style="margin-top:6px;">
              <input type="checkbox" checked={strict() && canStrict()} disabled={!canStrict() || starting()}
                     onChange={(e) => setStrict(e.currentTarget.checked)} />
              <span>
                <span class="restore-field-label" style="display:inline;">Strict restore (verify after)</span>
                <span class="muted" style="display:block;font-size:11px;">
                  {canStrict()
                    ? 'After the restore, compares structure + per-table row counts to the backup’s manifest and logs any mismatch to the audit.'
                    : 'Unavailable — this dump has no Ninocat correctness manifest (only a strict backup carries one).'}
                </span>
              </span>
            </label>
            </Show>
          </Show>

          <div class="restore-actions">
            <Show
              when={prep()}
              fallback={
                <>
                  <button class="btn btn-ghost" onClick={closeAll} disabled={uploadPct() !== null}>Cancel</button>
                  <button class="btn btn-primary" onClick={upload} disabled={!canUpload()}>
                    ⬆ Upload & measure
                  </button>
                </>
              }
            >
              <button class="btn btn-ghost" onClick={cancelPrep} disabled={starting()}>Back</button>
              <button class="btn btn-danger" onClick={start} disabled={starting() || startBlocked()}
                      title={startBlocked() ? 'Check the acknowledgement above to proceed' : undefined}>
                {starting() ? '⏳ Starting…' : (dbViolations().length > 0 ? '⬆ Restore anyway' : '⬆ Start restore')}
              </button>
            </Show>
          </div>
        </div>
      </Modal>
    </Show>
  );
}
